import React, { memo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Slider } from '@Sage/hoi-poi-ui';

const propTypes = {
    fieldId: PropTypes.string,
    hidden: PropTypes.bool,
    max: PropTypes.number,
    min: PropTypes.number,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    shouldRenderField: PropTypes.func,
    step: PropTypes.number,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const Percent = ({
    fieldId,
    hidden,
    max = 100,
    min = 0,
    onChange,
    readOnly,
    shouldRenderField,
    step = 1,
    value = 0,
    isEnriched = false,
}) => {
    const [innerValue, setInnerValue] = useState(Number(value) || 0);
    const debounceRef = useRef(null);
    const prevValue = useRef(value);

    useEffect(() => {
        if (value !== innerValue && prevValue.current !== value) {
            setInnerValue(Number(value) || 0);
        }
        prevValue.current = value;
    }, [innerValue, value]);

    // Cleanup debounce timeout on unmount to prevent memory leaks and state updates on unmounted component
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const handleChange = useCallback(
        (newValue) => {
            setInnerValue(newValue);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                onChange?.(newValue);
            }, 400);
        },
        [onChange],
    );

    const tipFormatter = useCallback((val) => `${val * 10}%`, []);

    if (hidden) return null;
    if (shouldRenderField && !shouldRenderField(fieldId)) return null;

    return (
        <Slider
            className="fm-field-percent"
            isReadOnly={readOnly}
            max={max}
            min={min}
            onChange={handleChange}
            step={step}
            tipFormatter={tipFormatter}
            value={innerValue}
            isEnriched={isEnriched}
        />
    );
};

Percent.displayName = 'Percent';
Percent.propTypes = propTypes;

export default memo(Percent);
